import { useState, useEffect, useCallback, useRef } from "react";
import api from "api/api";

//Props Validations
import PropTypes from "prop-types";

//Material UI Components
import Select from "react-select";
import Typography from "@mui/material/Typography";

//import assets

// import component
import CorrelationTable from "./CorrelationTable";
import Loader from "../common/Loader";
// import { GlobalContext } from "../../helper/GlobalContext";
import { consumerFilterPayload } from "../../utils/consumerFilterUtils";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { loadCorrelationAnalysisResultState, loadIsCorrelationAnalysisResultLoadingState, loadCorrelationAnalysisSampleInfoState, loadSelectedCorrelationMethodState } from "store/features/correlationSlice";
import { loadTargetKpiState } from "store/features/driverAnalysisSlice";
// import { loadIsFilterAcrossProductsState } from "store/features/consumerFilterSlice";
import { showNotification } from "store/features/notificationSlice";

import VariableCategoryColorMap from "../../utils/VariableCategoryColorMap";

function CorrelationTab({ productsSecondaryData = [] }) {
    // REDUX
    const dispatch = useDispatch();
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const weightToggle = useSelector((state) => state.correlationStore.weightToggleForCorrelation || false);
    const correlationData = useSelector((state) => state.correlationStore.correlationAnalysisResult);
    const sampleInfo = useSelector((state) => state.correlationStore.correlationAnalysisSampleInfo);
    const isLoading = useSelector((state) => state.correlationStore.isCorrelationAnalysisResultLoading);
    const backendVariableVariableCategory = useSelector((state) => state.dataMappingStore.backendVariableVariableCategory);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const consumerFilterStoreData = useSelector((state) => state.correlationStore.currentSelectedFiltersForCorrelation);
    const missingFiltersStoreData = useSelector((state) => state.correlationStore.missingFiltersForCorrelation);
    const role = useSelector((state) => state.userStore.role) || null;
    const selectedCorrelationMethod = useSelector((state) => state.correlationStore.selectedCorrelationMethod);

    // target NaN flag
    const [targetKpiNan, setTargetKpiNan] = useState(false);

    const [selectedCoefficient, setSelectedCoefficient] = useState({
        value: null,
        label: (
            <>
                Loading...
                <span> &nbsp; </span>
                <span
                    style={{
                        color: VariableCategoryColorMap["KPI (OO)"],
                    }}
                >
                    <em> KPI (OO)</em>
                </span>
            </>
        ),
    });
    const selectedCoefficientRef = useRef(null);
    selectedCoefficientRef.current = selectedCoefficient.value;
    const [dropdownData, setDropdownData] = useState([]);
    const [correlationMethodDropdownData, setCorrelationMethodDropdownData] = useState([]);

    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    // Calculate default correlation method based on product category
    const getDefaultCorrelationMethod = () => {
        if (isHairCare || isSkinCare || isUnileverFragranceHouse) {
            return { value: "pearson", label: "Pearson" };
        } else {
            return { value: "kendall", label: "Kendall" };
        }
    };

    // filter the unique products
    const productsSecondaryDataState = productsSecondaryData.filter((obj, index, self) => index === self.findIndex((o) => o.title === obj.title && o.coreDataId === obj.coreDataId));

    const selectedStudyIds = selectedStudy.map((datum) => datum.id).join(",");
    const selectedStudyTypeIds = selectedStudy.map((datum) => datum.study_type).join(",");
    const selectedStudyTypeMethodIds = selectedStudy.map((datum) => datum.study_type_method).join(",");

    useEffect(() => {
        const _dropdownData = Array.isArray(correlationData)
            ? correlationData.map((datum) => {
                return {
                    value: datum.backendVariable,
                    displayLabel: datum.displayLabel?.toLowerCase() || "",
                    label: (
                        <>
                            {datum.displayLabel?.charAt(0).toUpperCase() + datum.displayLabel?.slice(1) || ""}
                            <span> &nbsp; </span>
                            <span
                                style={{
                                    color: VariableCategoryColorMap[backendVariableVariableCategory[datum.backendVariable]],
                                }}
                            >
                                <em>{backendVariableVariableCategory[datum.backendVariable]}</em>
                            </span>
                        </>
                    ),
                };
            })
            : [];
        const _selectedCoefficient = {
            value: selectedCoefficient.value,
            label: (
                <>
                    {selectedCoefficient.label?.props?.children?.[0] || ""?.charAt(0).toUpperCase() + selectedCoefficient.label?.props?.children?.[0] || ""?.slice(1) || ""}
                    <span> &nbsp; </span>
                    <span
                        style={{
                            color: VariableCategoryColorMap[backendVariableVariableCategory[selectedCoefficient.value]],
                        }}
                    >
                        <em>{backendVariableVariableCategory[selectedCoefficient.value]}</em>
                    </span>
                </>
            ),
        };

        setDropdownData([_selectedCoefficient, ..._dropdownData]);
    }, [selectedCoefficient, correlationData]);

    useEffect(() => {
        dispatch(loadTargetKpiState(selectedCoefficient.value));
    }, [dispatch, selectedCoefficient]);

    useEffect(() => {
        if (!selectedCorrelationMethod || Object.keys(selectedCorrelationMethod).length === 0) {
            const defaultMethod = getDefaultCorrelationMethod();
            dispatch(loadSelectedCorrelationMethodState(defaultMethod));
        }
    }, []);

    // correlation method dropdown
    useEffect(() => {
        // all available correlation methods
        const allMethods = [
            { value: "pearson", label: "Pearson" },
            { value: "spearman", label: "Spearman" },
            { value: "kendall", label: "Kendall" },
        ];

        // methods to show based on user role
        let availableMethods = [];
        if (role === "admin") {
            availableMethods = allMethods;
        } else {
            availableMethods = allMethods.filter((method) => method.value !== "kendall");
        }

        // Set the dropdown data
        setCorrelationMethodDropdownData(availableMethods);
    }, [role]);

    const handleDropdownInputChange = (e) => {
        setSelectedCoefficient(e);
        // // Update selected backend variable state based on selection
        // setSelectedBackendVariableForDriverAnalysis([...selectedBackendVariableForDriverAnalysis, e.value]);
        fetchData(null, consumerFilterStoreData, missingFiltersStoreData, weightToggle, e.value, selectedCorrelationMethod?.value);
    };
    const handleCorrelationMethodDropdownInputChange = (e) => {
        dispatch(loadSelectedCorrelationMethodState(e));
        fetchData(null, consumerFilterStoreData, missingFiltersStoreData, weightToggle, selectedCoefficient.value, e.value);
    };

    const fetchData = useCallback(
        async (obj = null, consumerFilter = [], missingFiltersStoreData = {}, weight = weightToggle, targetVariable = selectedCoefficientRef.current, correlationMethod = null) => {
            let _consumer_filters = [];
            let _missing_filters = [];
            const pcs = productsSecondaryData
                .map((datum) => {
                    const pc = `checked--${datum.title.split(" ").join("_")}__${datum.coreDataId}`;
                    let _productColumnVisibility = { ...productColumnVisibility };
                    if (obj) {
                        _productColumnVisibility = obj;
                    }
                    if (_productColumnVisibility) {
                        if (!_productColumnVisibility?.[pc]) {
                            return datum;
                        }
                    } else {
                        return datum;
                    }
                })
                .filter((ele) => ele !== undefined && ele !== null);

            let _products = [];
            pcs.forEach((datum) => {
                const product = datum.title.split(" ").filter(item => item != "").join("_");
                const _product = `${product}__${datum.coreDataId}`;
                let productCf = {
                    product_code: datum.title,
                    core_data_model_id: datum.coreDataId.toString(),
                };
                _products.push(productCf);
                let _tempFilters = consumerFilterPayload(consumerFilter, product, datum.coreDataId);
                if (_tempFilters && Object.keys(_tempFilters).length > 0) {
                    productCf = {
                        ...productCf,
                        ..._tempFilters,
                    };
                    _consumer_filters.push(JSON.parse(JSON.stringify(productCf)));
                }
                if (Object.hasOwn(missingFiltersStoreData, _product)) {
                    if (missingFiltersStoreData[_product].isMissing === "true") {
                        if (missingFiltersStoreData[_product].filterVariant === "single" && !missingFiltersStoreData[_product].submitFlag) {
                            _missing_filters.push(productCf);
                        } else if (missingFiltersStoreData[_product].filterVariant === "multiple") {
                            _missing_filters.push(productCf);
                        }
                    }
                }
            });
            // if (isReset === true) {
            //     _consumer_filters = [];
            //     _missing_filters = [];
            // }

            try {
                // setIsLoading(true);
                dispatch(loadIsCorrelationAnalysisResultLoadingState(true));
                // const url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/correlation-data/`;
                const url = process.env.REACT_APP_SERVER_PATH + `/secondary-study-datas/correlation/`;

                let methodToSend = correlationMethod;
                if (!methodToSend) {
                    methodToSend = selectedCorrelationMethod?.value;
                }
                if (!methodToSend) {
                    methodToSend = isHairCare || isSkinCare || isUnileverFragranceHouse ? "pearson" : "kendall";
                }

                const payload = {
                    core_data_model_id: selectedStudyIds,
                    variable_category: "KPI (OO),Secondary Attribute,Agreement KPI",
                    target_variable: targetVariable,
                    applied_consumer_filters: _consumer_filters,
                    weight_toggle: weight,
                    products: pcs.length === 0 ? ["NA"] : productsSecondaryData.length !== pcs.length ? _products : [],
                    product_category: parseInt(productCategoryId),
                    study_type: selectedStudyTypeIds,
                    study_type_method: selectedStudyTypeMethodIds,
                    missing_consumer_filters: _missing_filters,
                    correlation_method: methodToSend,
                };

                await api
                    .post(url, payload)
                    .then((res) => {
                        const _corrData = res.data["correlation_data"];
                        const _sampleInfo = res.data["sample_info"];
                        const info = res.data["info"];
                        const _target_variable_data = res.data["target_variable_data"];
                        const backendTargetVariable = _target_variable_data["target_variable"];
                        const backendTargetVariableDisplayLabel = _target_variable_data["display_label"];
                        if (!selectedCoefficient.value && backendTargetVariable && backendTargetVariableDisplayLabel) {
                            setSelectedCoefficient({
                                value: backendTargetVariable,
                                label: (
                                    <>
                                        {backendTargetVariableDisplayLabel}
                                        <span>&nbsp;</span>
                                        <span
                                            style={{
                                                color: VariableCategoryColorMap[backendVariableVariableCategory[backendTargetVariable]],
                                            }}
                                        >
                                            <em>{backendVariableVariableCategory[backendTargetVariable]}</em>
                                        </span>
                                    </>
                                ),
                            });
                        }
                        dispatch(loadCorrelationAnalysisResultState([..._corrData]));
                        dispatch(loadCorrelationAnalysisSampleInfoState([ ..._sampleInfo ]));
                        dispatch(loadIsCorrelationAnalysisResultLoadingState(false));
                        if (info?.type === "error" && info?.message) {
                            handleError("error", info.message);
                            setTargetKpiNan(true);
                        } else {
                            setTargetKpiNan(false);
                        }
                    })
                    .catch((error) => {
                        handleError("error", error);
                        dispatch(loadIsCorrelationAnalysisResultLoadingState(false));
                    });
            } catch (error) {
                handleError("error", error);
                dispatch(loadIsCorrelationAnalysisResultLoadingState(false));
            }
        },
        [selectedStudyIds, productCategoryId, productsSecondaryData, productColumnVisibility]
    );

    useEffect(() => {
        // if (!correlationData.length) {
        //     fetchData();
        // }
        fetchData(null, consumerFilterStoreData, missingFiltersStoreData, weightToggle, selectedCoefficient.value, selectedCorrelationMethod?.value);
    }, [fetchData]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };
    // // Function to reset selected backend variables
    // const resetSelectedBackendVariables = () => {
    //     setSelectedBackendVariableForDriverAnalysis([]); // Reset the selected backend variables
    // };

    return (
        <>
            {isLoading && <Loader />}
            <div
                className="card-btns"
                style={{ padding: "0px 0px", margin: "10px 0px", marginTop: "30px", marginRight: "60px" }}
            >
                <div
                    className=" d-flex"
                    style={{ alignItems: "center" }}
                >
                    <Typography
                        className="text-upper-case color-blue"
                        sx={{ fontSize: "12px", fontWeight: "700", marginRight: "5px" }}
                    >
                        Target Variable:
                    </Typography>
                    <div className="dropdown-correlation">
                        <div className="dropdown-card-dropdown-container ">
                            <Select
                                className="dropdown dropdown-first-row "
                                name={"correlationWith"}
                                placeholder={"Target Variable"}
                                onChange={(newValue) => handleDropdownInputChange(newValue)}
                                value={selectedCoefficient}
                                options={dropdownData}
                                filterOption={(option, input) => !input || (option.data.displayLabel || "").includes(input.toLowerCase())}
                            />
                        </div>
                    </div>
                </div>
                {/* Correlation Method Dropdown: Only show for admin users */}
               {(isHairCare || isSkinCare || isUnileverFragranceHouse) && role === "admin" && (
                    <div
                        className=" d-flex"
                        style={{ alignItems: "center" }}
                    >
                        <div className="dropdown-correlation">
                            <div className="dropdown-card-dropdown-container ">
                                <Select
                                    className="dropdown dropdown-first-row "
                                    name={"correlationMethod"}
                                    placeholder={"Correlation Method"}
                                    onChange={(newValue) => handleCorrelationMethodDropdownInputChange(newValue)}
                                    value={selectedCorrelationMethod}
                                    options={correlationMethodDropdownData}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div
                style={{
                    minHeight: "calc(100vh - 300px)",
                }}
            >
                <CorrelationTable
                    productsSecondaryData={productsSecondaryDataState}
                    targetKpi={selectedCoefficient.value}
                    targetKpiLabel={selectedCoefficient.label?.props?.children?.[0] || ""}
                    handleFilterSubmit={fetchData}
                    selectedStudy={selectedStudy}
                    correlationData={correlationData}
                    sampleInfo={sampleInfo}
                    targetKpiNan={targetKpiNan}
                    correlationMethodLabel={selectedCorrelationMethod?.label}
                    getDefaultCorrelationMethod={getDefaultCorrelationMethod}
                    resetTargetVariable={() => setSelectedCoefficient((prev) => ({ ...prev, value: null }))}
                // resetCorrelation={fetchData} // This should trigger the reset logic
                // resetSelectedBackendVariables={resetSelectedBackendVariables} // Ensure this is passed
                />
            </div>
        </>
    );
}

// Typechecking props for the PerformanceTab
CorrelationTab.propTypes = {
    productsSecondaryData: PropTypes.array,
};

export default CorrelationTab;
